import { Stack, Typography } from "@mui/material";
import { timeTicks } from "d3";
import { format } from "date-fns";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

function CustomTooltip({ payload, unit }) {
  if (!payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div
      style={{
        fontSize: "12px",
        background: "#fff",
        border: "1px solid #ccc",
        padding: "8px 12px",
        borderRadius: 4,
      }}
    >
      <p style={{ margin: 0, fontWeight: "bold" }}>
        {format(new Date(point.timestamp), "M/d p")}
      </p>
      {payload
        .filter((entry) => entry.name !== "timestamp")
        .map((entry) => (
          <p key={entry.name} style={{ margin: "4px 0 0", color: entry.color }}>
            {entry.name}: {entry.value} {unit}
          </p>
        ))}
    </div>
  );
}

CustomTooltip.propTypes = {
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string,
      payload: PropTypes.shape({
        timestamp: PropTypes.number.isRequired,
      }).isRequired,
    })
  ),
  unit: PropTypes.string,
};

CustomTooltip.defaultProps = {
  payload: [],
  unit: "%",
};

export default function AugmentationChart({
  measures,
  name,
  label,
  risk,
  unit,
  domain,
}) {
  const chartData = useMemo(
    () =>
      measures.map((measure) => ({
        ...measure.value,
        timestamp: new Date(measure.timestamp).valueOf(),
      })),
    [measures]
  );

  const normalData = chartData.filter((d) =>
    name === "subendocardial_viability_ratio"
      ? d[name] >= risk
      : d[name] <= risk
  );
  const highData = chartData.filter((d) =>
    name === "subendocardial_viability_ratio" ? d[name] <= risk : d[name] > risk
  );

  const ticks = useMemo(() => {
    if (!chartData.length) return [];
    return timeTicks(
      new Date(chartData[0].timestamp),
      new Date(chartData[chartData.length - 1].timestamp),
      10
    ).map((d) => d.getTime());
  }, [chartData]);

  return (
    <Stack spacing={2} mt={2}>
      <Typography fontWeight="bold">{label}</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 5, right: 0, left: 4, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            style={{ fontSize: "12px" }}
            angle={-30}
            dy={15}
            ticks={ticks}
            interval={0}
            padding={{ left: 50, right: 30 }}
            tickFormatter={(value) => format(new Date(value), "M/d p")}
          />
          <YAxis
            dataKey={name}
            type="number"
            domain={domain}
            tickFormatter={(v) => `${v} ${unit}`}
            style={{ fontSize: "12px" }}
          />
          <Legend
            verticalAlign="top"
            height={30}
            wrapperStyle={{ lineHeight: "20px" }}
          />
          <Tooltip content={<CustomTooltip unit={unit} />} />
          <Scatter
            name="Normal"
            data={normalData}
            fill="#2E7D32"
            shape="circle"
          />
          <Scatter name="Risk" data={highData} fill="#D32F2F" shape="circle" />
        </ScatterChart>
      </ResponsiveContainer>
    </Stack>
  );
}

AugmentationChart.propTypes = {
  measures: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      timestamp: PropTypes.string.isRequired,
      value: PropTypes.shape({
        augmentation_index: PropTypes.number.isRequired,
      }).isRequired,
    })
  ).isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  risk: PropTypes.number,
  unit: PropTypes.string,
  domain: PropTypes.arrayOf(PropTypes.number),
};

AugmentationChart.defaultProps = {
  unit: "%",
  risk: 0,
  domain: [0, 100],
};
